import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import { Panel, PanelHeader, PanelBody } from './ui/Panel';
import Pill from './ui/Pill';
import { ErrorNotice, SkeletonRows } from './ui/States';
import { dateShort } from '../lib/format';
import type {
  DataResponse,
  PaymentList,
  PaymentListQuery,
  ProgramParticipantPage,
  ProgramParticipantsQuery,
} from '../contracts';
import {
  Participant,
  PARTICIPANT_TONE,
  PAYMENT_KINDS,
  PAYMENT_STATUS_LABEL,
  PAYMENT_TONE,
  label,
  money,
} from '../lib/programs';

/** The member's program cycles and status in each. */
export function MemberPrograms({ userId }: { userId: string }) {
  const list = useQuery<{ participants: Participant[] }>(
    ['participants', 'member', userId],
    async () =>
      (
        await api.get<DataResponse<ProgramParticipantPage>>('/programs/participants', {
          params: { userId } satisfies ProgramParticipantsQuery,
        })
      ).data.data,
  );
  return (
    <Panel>
      <PanelHeader title="Programs" />
      <PanelBody className="pt-2">
        {list.isLoading ? (
          <SkeletonRows rows={2} />
        ) : list.error ? (
          <ErrorNotice>Could not load programs.</ErrorNotice>
        ) : !list.data?.participants.length ? (
          <p className="text-small text-ink-2">Not in any program cycle.</p>
        ) : (
          <ul className="text-table">
            {list.data.participants.map((p) => (
              <li
                key={p.id}
                className="flex items-center justify-between gap-3 py-2 border-b border-line last:border-0"
              >
                <div>
                  <div>
                    {p.cycle?.program?.name} {p.cycle?.label}
                  </div>
                  <div className="text-small text-ink-2">
                    {label(p.role)} · since {dateShort(p.statusChangedAt)}
                  </div>
                </div>
                <Pill tone={PARTICIPANT_TONE[p.status] ?? 'planned'}>{label(p.status)}</Pill>
              </li>
            ))}
          </ul>
        )}
        <Link className="text-small underline block mt-3" to="/programs">
          Programs
        </Link>
      </PanelBody>
    </Panel>
  );
}

/** Ledger entries for the member. Admin only: amounts are financial-status data, and each read is audited. */
export function MemberPayments({ userId }: { userId: string }) {
  const params: PaymentListQuery = { userId };
  const list = useQuery<PaymentList>(
    ['payments', 'member', userId],
    async () => (await api.get<DataResponse<PaymentList>>('/payments', { params })).data.data,
  );
  return (
    <Panel>
      <PanelHeader title="Payments" />
      <PanelBody className="pt-2">
        {list.isLoading ? (
          <SkeletonRows rows={2} />
        ) : list.error ? (
          <ErrorNotice>Could not load payments.</ErrorNotice>
        ) : !list.data?.entries.length ? (
          <p className="text-small text-ink-2">No ledger entries.</p>
        ) : (
          <ul className="text-table">
            {list.data.entries.map((e) => (
              <li
                key={e.id}
                className="flex items-center justify-between gap-3 py-2 border-b border-line last:border-0"
              >
                <div>
                  <div>
                    {PAYMENT_KINDS[e.kind] ?? e.kind} · {money(e.amountCents)}
                  </div>
                  <div className="text-small text-ink-2">
                    {e.activity.cycle
                      ? `${e.activity.cycle.program ?? ''} ${e.activity.cycle.label} · `
                      : e.activity.event
                        ? `${e.activity.event.title} · `
                        : ''}
                    FY {e.fiscalYear}
                  </div>
                </div>
                <Pill tone={PAYMENT_TONE[e.status]}>{PAYMENT_STATUS_LABEL[e.status]}</Pill>
              </li>
            ))}
          </ul>
        )}
        <Link className="text-small underline block mt-3" to="/payments">
          Payments ledger
        </Link>
      </PanelBody>
    </Panel>
  );
}
